import React, { useEffect, useCallback, useState, useRef } from 'react';
import { FaTrash } from 'react-icons/fa';
import * as Yup from 'yup';

import { Button, Row, Col, Container } from 'react-bootstrap';
import { parseISO, format } from 'date-fns';

import { useToast } from '../../hooks/toast';

import api from '../../services/api';

import Loading from '../Loading';

import { formataIdade, formataTelefone } from '../../utils/funcoes';
import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import {
  IAgendamentoListagem,
  IPaciente,
  IPacienteMaisCartaoDTO,
  IRetornoStatusParcelaCartaoDTO,
} from '../Agendamento';
import BuscaPaciente from '../BuscaPaciente';

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
}

interface IFormData {
  AgePacCodigo: number;
}

type ComponentProps = {
  AgeCodigo: number;
  updated_at: Date;
  cartao: boolean;
  paciente: IPaciente;
  handleFechar(agendamentoListagem: IAgendamentoListagem | undefined): void;
};

const TransferirPacienteAgendamento: React.FC<ComponentProps> = ({
  AgeCodigo,
  updated_at,
  cartao,
  paciente,
  handleFechar,
}) => {
  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [pacienteID, setPacienteID] = useState<number | undefined>();
  const [pacienteAntigo, setPacienteAntigo] = useState<IPaciente>();
  const [pacienteNovo, setPacienteNovo] = useState<IPaciente>();
  const [dadosPlano, setDadosPlano] = useState<
    IRetornoStatusParcelaCartaoDTO | undefined
  >();

  useEffect(() => {
    if (paciente)
      setPacienteAntigo({
        ...paciente,
        PacCelular: formataTelefone(paciente.PacCelular),
        PacDataNascFormatada: format(
          parseISO(paciente.PacDataNasc.toString()),
          'dd/MM/yyyy',
        ),
        PacIdade: formataIdade(parseISO(paciente.PacDataNasc.toString())),
      });
  }, [paciente]);

  const handleSelectClienteChange = useCallback(
    ({ value, label }: { value: string; label: string }) => {
      if (value) {
        setPacienteID(Number(value));
      }
    },
    [],
  );

  useEffect(() => {
    async function loadDados(): Promise<void> {
      try {
        setLoading(true);
        const response = await api.get<IPacienteMaisCartaoDTO>(
          `/pacientes/pacientemaiscartao/${pacienteID}`,
        );

        const {
          cartao: cartaoPacienteNovo,
          atendimentos,
          faltas,
          agendamentosPaciente,
          possuiCartao,
          ...rest
        } = response.data;

        setPacienteNovo({
          ...rest,
          PacCelular: formataTelefone(rest.PacCelular),
          PacDataNascFormatada: format(
            parseISO(rest.PacDataNasc.toString()),
            'dd/MM/yyyy',
          ),
          PacIdade: formataIdade(parseISO(rest.PacDataNasc.toString())),
        });

        if (possuiCartao && cartaoPacienteNovo) {
          setDadosPlano(cartaoPacienteNovo[0]);
        }
      } catch (error) {
        addToast({
          title: 'Não foi possível buscar o paciente',
          type: 'error',
        });
        setPacienteID(undefined);
      } finally {
        setLoading(false);
      }
    }
    if (pacienteID && AgeCodigo) loadDados();
  }, [pacienteID, AgeCodigo, addToast]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const schema = Yup.object().shape({
        AgePacCodigo: Yup.number().required(),
      });

      await schema.validate(
        { AgePacCodigo: pacienteID },
        {
          abortEarly: false,
        },
      );

      const agendamento = { AgePacCodigo: pacienteID, updated_at };

      const appointment = await api.patch(
        `/agendamentos/paciente/${AgeCodigo}`,
        agendamento,
      );
      // pode pegar o retorno e jogar para a agenda
      addToast({ title: 'Agendamento salvo', type: 'success' });
      handleFechar(appointment.data);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);

        addToast({
          type: 'error',
          title: 'Houve um erro ao transferir agendamento',
          description: 'Selecione o novo paciente',
        });
        return;
      }
      const mensagemErro = getResponseErrors(error);
      addToast({
        type: 'error',
        title: 'Houve um erro ao transferir agendamento',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, handleFechar, pacienteID, AgeCodigo]);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      <>
        {loading && <Loading backgroundBlack={false} />}

        <Row>
          <Col>
            <h5>Paciente Original</h5>

            {pacienteAntigo && (
              <div>
                <span>
                  <b>{pacienteAntigo.PacNome}</b>
                </span>
                <br />
                <span>
                  Data de Nascimento:
                  <b>{pacienteAntigo.PacDataNascFormatada}</b>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Idade:&nbsp;
                  <b>{pacienteAntigo.PacIdade}</b>
                </span>
                <br />
                <span>
                  CPF: <b>{pacienteAntigo.PacCPF}</b>
                </span>
              </div>
            )}
          </Col>
          <Col>
            <div
              className={`justify-content-between ${
                pacienteNovo ? 'd-flex' : 'd-none'
              }`}
            >
              <h5>Paciente Novo</h5>
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                onClick={() => {
                  setPacienteID(undefined);
                  setPacienteNovo(undefined);
                }}
              >
                <FaTrash size={14} />
              </Button>
            </div>
            {pacienteNovo && (
              <div>
                <span>
                  <b>{pacienteNovo.PacNome}</b>
                </span>
                <br />
                <span>
                  Data de Nascimento: <b>{pacienteNovo.PacDataNascFormatada}</b>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Idade:&nbsp;
                  <b>{pacienteNovo.PacIdade}</b>
                </span>
                <br />
                <span>
                  CPF: <b>{pacienteNovo.PacCPF}</b>
                </span>
              </div>
            )}
          </Col>
        </Row>

        <br />
        {!pacienteAntigo ||
          (!pacienteNovo && (
            <BuscaPaciente
              loading={loading}
              handleSelectCliente={handleSelectClienteChange}
            />
          ))}

        {pacienteAntigo && pacienteNovo && (
          <Button
            type="button"
            variant="danger"
            size="sm"
            className="float-right ml-2"
            onClick={() => handleSubmit()}
            disabled={loading}
          >
            Tansferir
          </Button>
        )}
      </>
    </Container>
  );
};

export default TransferirPacienteAgendamento;
